<script setup>
import { computed } from 'vue'
import { router } from '@inertiajs/vue3'
import { Menu, MenuButton, MenuItems } from '@headlessui/vue'
import { PencilAltIcon, UserGroupIcon, ViewListIcon } from '@heroicons/vue/solid'
import { Typography, Icon } from '@aryeo/runway'
import { usePermissions } from '@/Hooks/Company/usePermissions'

import { useAuth } from '@/Hooks/Company/useAuth'
import UserMenuItem from '@/Components/Shared/Header/UserMenuItem.vue'

defineProps({
  darkMode: { type: Boolean, default: true },
})

const { authCompanyTeamMember, authCompanyTeamMembers } = useAuth()
const { hasPermission } = usePermissions()

const otherCompanyTeamMembers = computed(() => {
  return authCompanyTeamMembers.value.data
    .filter((companyTeamMember) => companyTeamMember.id !== authCompanyTeamMember.value.id)
    .filter((companyTeamMember) => companyTeamMember.company)
    .sort((a, b) => {
      return a.company?.name.localeCompare(b.company?.name)
    })
})

const hasOtherCompanyTeamMembers = computed(() => otherCompanyTeamMembers.value.length > 0)

const switchSelectedCompanyTeamMember = async (selectedCompanyTeamMemberId) => {
  router.flushAll()
  router.post(route('web.company.auth-company-user.selected-company-team-member.update'), {
    company_team_member_id: selectedCompanyTeamMemberId,
  })
}
</script>

<template>
  <Menu as="div" class="relative inline-block w-full my-auto text-left">
    <div>
      <MenuButton
        class="flex items-center justify-between w-full h-10 px-3 text-sm rounded-xl focus:outline-none focus-visible:shadow-focus"
      >
        <span class="text-sm font-medium truncate">
          <span class="sr-only">Open company menu for </span>
          {{ authCompanyTeamMember.company.name }}
        </span>
        <Icon name="chevronDown" size="medium" />
      </MenuButton>
    </div>

    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="scale-95 opacity-0"
      enter-to-class="scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="scale-100 opacity-100"
      leave-to-class="scale-95 opacity-0"
    >
      <MenuItems
        class="absolute max-h-screen-75 overflow-y-auto left-0 z-30 w-56 mt-1 origin-top-left bg-white rounded-xl shadow-card border p-2 focus:outline-none"
      >
        <div class="py-1">
          <Typography variant="body-small" as="p" class="px-4 pt-2 pb-1 whitespace-nowrap"
            >Your current company is</Typography
          >
          <div class="flex items-center px-4 py-2 text-sm font-bold text-header-dark">
            <UserGroupIcon class="w-5 h-5 mr-3" aria-hidden="true" />
            <Typography variant="body-small-bold" as="p" class="truncate">
              {{ authCompanyTeamMember.company.name }}
            </Typography>
          </div>
          <UserMenuItem
            v-if="hasPermission('settings_manage')"
            :href="route('web.company.auth-company.edit')"
          >
            <PencilAltIcon class="w-5 h-5 mr-3" aria-hidden="true" />
            Edit Current Company
          </UserMenuItem>
        </div>
        <div v-if="hasOtherCompanyTeamMembers" class="py-1">
          <Typography variant="body-small" as="p" class="px-4 py-2 whitespace-nowrap"
            >Switch company to</Typography
          >
          <UserMenuItem
            v-for="companyTeamMember in otherCompanyTeamMembers"
            :key="companyTeamMember.id"
            as="div"
            class="cursor-pointer"
            @click.prevent="switchSelectedCompanyTeamMember(companyTeamMember.id)"
          >
            <UserGroupIcon class="flex-none w-5 h-5 mr-3" aria-hidden="true" />
            <Typography as="p" class="truncate">{{ companyTeamMember.company.name }}</Typography>
          </UserMenuItem>
        </div>
        <div v-if="hasOtherCompanyTeamMembers">
          <UserMenuItem as="a" :href="route('web.company.auth-companies.index')">
            <ViewListIcon class="w-5 h-5 mr-3" aria-hidden="true" />
            View All Companies
          </UserMenuItem>
        </div>
      </MenuItems>
    </transition>
  </Menu>
</template>
